import { useState } from 'react'

import { useCall } from './useCall'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import { useDisclosure } from '@chakra-ui/react'

import { MixpanelDataIProps } from '@/features/incident-drawer'
import { useEmergencyCallIncidentCategoryLazyQuery } from '@/graphql/generated/hooks'

/**
 * Shared behavior for the global emergency-call trigger: resolves the
 * emergency-call incident category, opens the confirmation modal, and places
 * the call once an incident exists. Consumed by both the legacy
 * {@link GlobalEmergencyCallButton} and the rebrand
 * {@link DarkGlobalEmergencyCallButton} so the flow lives in one place.
 */
export const useGlobalEmergencyCall = (mixpanelData?: MixpanelDataIProps) => {
  const { t } = useTranslation()
  const { isOpen, onClose, onOpen } = useDisclosure()
  const { openCall } = useCall()
  const { showError } = useToasts()
  const [getEmergencyCallIncidentCategory, { loading: isLoading }] =
    useEmergencyCallIncidentCategoryLazyQuery()
  const [emergencyCallCategoryId, setEmergencyCallCategoryId] = useState<
    string | null
  >(null)

  const handleEmergencyButtonClick = async () => {
    try {
      const { data: categoryData } = await getEmergencyCallIncidentCategory()
      const categoryId = categoryData?.emergencyCallIncidentCategory?.id

      if (!categoryId) {
        showError(t('emergency_call:no_category_found'))
        return
      }

      setEmergencyCallCategoryId(categoryId)
      onOpen()
    } catch (error) {
      showError(t('emergency_call:failed_to_load_config'))
    }
  }

  const handleCallClick = (
    facilityId?: string,
    facilityName?: string,
    incidentId?: string,
    incidentDisplayId?: string,
    emergencyNumber?: string
  ) => {
    if (facilityId && incidentId && incidentDisplayId) {
      // Only call openCall after incident is created with real data
      const payload = {
        incidentId,
        incidentDisplayId,
        facilityName,
        emergencyNumber,
        facilityId,
      }
      openCall(payload, mixpanelData)
      return
    }
  }

  return {
    isOpen,
    onClose,
    isLoading,
    emergencyCallCategoryId,
    handleEmergencyButtonClick,
    handleCallClick,
  }
}
